import React from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { useSetup } from '../context/SetupContext';

interface SetupGuardProps {
    children: React.ReactNode;
}

const SetupGuard: React.FC<SetupGuardProps> = ({ children }) => {
    const { setupRequired, isLoading, error, refetch } = useSetup();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    // Mock režim (?mock=...) bypass — žádný BE call, čistě FE preview pro vývoj a demo
    if (searchParams.get('mock')) {
        return <>{children}</>;
    }

    // Show loading while checking setup status
    if (isLoading) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                minHeight="100vh"
            >
                <CircularProgress />
                <Typography variant="body1" sx={{ mt: 2 }}>
                    Kontroluji stav aplikace...
                </Typography>
            </Box>
        );
    }

    // Handle error state
    if (error) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                minHeight="100vh"
                gap={2}
            >
                <Typography variant="h5" color="error" gutterBottom>
                    Chyba při kontrole stavu aplikace
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {error.message || 'Nelze se připojit k serveru.'}
                </Typography>
                <Button variant="contained" onClick={() => refetch()}>
                    Zkusit znovu
                </Button>
            </Box>
        );
    }

    // If setup is required and we're not already on setup page, redirect
    if (setupRequired && location.pathname !== '/setup') {
        return <Navigate to="/setup" replace />;
    }

    // If setup is NOT required and we're on setup page, redirect to home
    if (!setupRequired && location.pathname === '/setup') {
        return <Navigate to="/" replace />;
    }

    return <>{children}</>;
};

export default SetupGuard;
